import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Table, Input, Switch, Form, Divider, Modal } from 'antd';
import { Link } from 'react-router-dom';
import { Cards } from '../../../components/cards/frame/cards-frame.js';
import { useParams } from 'react-router-dom';
import { BasicFormWrapper, Main, TableWrapper } from '../../styled.js';
import { UserTableStyleWrapper } from '../style.js';
import Spinner from '../../common/Spinner.js';
import Heading from '../../../components/heading/heading.js';
import { getFormsDetails, saveUserForms, checkFormsConnectedToListings } from '../../../redux/userForms/actionCreator.js';
import { Button } from '../../../components/buttons/buttons.js';
import UilTrashAlt from '@iconscout/react-unicons/icons/uil-trash-alt';
import Notification from '../../../utility/notification.js';
import HttpStatus from '../../../utility/status.enum.js';

const UserForms = () => {
    const { dateFormat, perPageRecord } = global;
    const buttonName = 'Save';
    const hostSlug = useParams()?.hostSlug ?? '';
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: perPageRecord,
        totalItems: 0,
    });
    const TYPE = {
        CUSTOM_FORMS: 1,
        JOT_FORMS: 2,
        CUSTOM_FORMS_TITLE: 'Host Template',
        JOT_FORMS_TITLE: 'Happy Guest  Template'
    };
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("ASC");
    const [spinnerLoader, setSpinnerLoader] = useState(false);
    const [formsList, setFormsList] = useState([]);
    const customFormTemplate = { slug: '', title: '', form_url: '', type: TYPE.CUSTOM_FORMS };
    const [userForms, setUserForms] = useState([customFormTemplate]);
    const [isDirty, setIsDirty] = useState(false);
    const [selectedJotForms, setSelectedJotForms] = useState([]);
    const [isCustomFormModalOpen, setIsCustomFormModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [submitType, setSubmitType] = useState(null);
    const [isTitleRequired, setTitleRequired] = useState(false);
    const [isURLRequired, setURLRequired] = useState(false);
    const [isJotFormsModified, setIsJotFormsModified] = useState(false);
    // Status values 
    const STATUS = {
        IS_DISPLAY: 1,
        IS_NOT_DISPLAY: 2,
    };
    const ERROR_MESSAGES = {
        FILL_TITLE: 'Please fill form title first',
        FILL_URL: 'Please fill custom form url first',
        FORM_CANNOT_BE_DELETED: 'Form currently configured to a listing and can’t be deleted',
        FORM_CANNOT_BE_TURNED_OFF: 'Form currently configured to a listing and can’t be turned off',
        FORM_URL_NOT_VALID: 'Please enter a valid URL',
        FORM_NOT_CHANGES: 'We didn’t detect any changes. Please update the form before saving'
    };

    useEffect(() => {
        global.isDirtyGlobal = isDirty;
    }, [isDirty])

    useEffect(() => {
        fetchFormsData();
    }, [hostSlug, pagination.currentPage, pagination.pageSize, sortField, sortOrder]);

    const fetchFormsData = async () => {
        try {
            setSpinnerLoader(true);
            const formData = {
                host_slug: hostSlug,
                page: pagination.currentPage,
                perPage: pagination.pageSize,
                sortField: sortField,
                sortOrder: sortOrder,
            };
            const response = await dispatch(
                getFormsDetails(formData, (response) => {
                    return response;
                }),
            );
            if (response?.status !== HttpStatus.HTTP_OK) {
                Notification.addNotificationError(response.message);
            }
            else {
                const formDataLocal = (response?.result?.formsData || []).map((data, index) => {
                    return {
                        ...data,  // Spread the existing properties of 'data'
                        uuid: index // Add the 'uuid' property
                    };
                });
                setFormsList(formDataLocal);
                if (response?.result?.selected_jotforms) {
                    setSelectedJotForms(JSON.parse(response?.result?.selected_jotforms));
                }
                setPagination((prevState) => ({
                    ...prevState,
                    totalItems: response?.result?.total || 0,
                }));
            }
        } catch (error) {
        } finally {
            setSpinnerLoader(false);
        }
    };

    const formTableData = [];
    formsList.map((formData, index) => {
        return formTableData.push({
            id: formData.id,
            key: formData?.uuid,
            title: formData?.title || '',
            is_display: formData?.is_display === STATUS.IS_DISPLAY ? true : false,
            slug: formData.slug,
            type: formData?.type === TYPE.CUSTOM_FORMS ? TYPE.CUSTOM_FORMS_TITLE : TYPE.JOT_FORMS_TITLE
        });
    });

    const formsTablecolumns = [
        {
            title: 'Form Title',
            dataIndex: 'title',
            key: 'title',
            sorter: true,
        },
        {
            title: 'Form Type',
            dataIndex: 'type',
            key: 'type',
            sorter: true,
        },
        {
            title: 'Display',
            dataIndex: 'is_display',
            key: 'is_display',
            render: (text, record) => (
                <Row gutter={15}>
                    <Col xs={12}>
                        <div className="project-modal">
                            <Switch size="large" checked={formsDisplayMap.get(record.id ?? record.slug)} onChange={(e) => handleFormEnable(e, record.id, record.slug)} className='switch-toggle' />
                        </div>
                    </Col>
                </Row>
            )
        },
    ];

    const handlePaginationChange = (page, pageSize) => {
        setPagination({
            ...pagination,
            currentPage: page,
            pageSize,
        });
    };

    const handlePageSizeChange = (current, size) => {
        setPagination(({
            ...pagination,
            currentPage: current,
            pageSize: size,
        }));
    };

    const handleSortChange = (pagination, filters, sorter) => {
        let sortOrder = "DESC";
        if (sorter.order === "ascend") {
            sortOrder = "ASC";
        }
        setSortField(sorter.field);
        setSortOrder(sortOrder);
    };

    const handleJotFormToggle = (checked, id, slug) => {
        setIsDirty(true);
        const updatedSelectedJotForms = [...selectedJotForms];
        const updatedFormsList = [...formsList];

        if (id === null) {
            // Handle custom form (id === null)
            const customFormIndex = updatedSelectedJotForms.findIndex(form => form.slug === slug);
            if (customFormIndex !== -1) {
                if (!checked) {
                    updatedSelectedJotForms.splice(customFormIndex, 1); // Remove custom form from selectedJotForms
                }
            }

            // Update custom form visibility in formsList
            const customFormInListIndex = updatedFormsList.findIndex(form => form.slug === slug);
            if (customFormInListIndex !== -1) {
                updatedFormsList[customFormInListIndex].is_display = checked ? STATUS.IS_DISPLAY : STATUS.IS_NOT_DISPLAY;
            }
            setFormsList(updatedFormsList); // Update forms list
            setSelectedJotForms(updatedSelectedJotForms); // Update selected forms
        } else {
            // Handle regular form (id !== null)
            const formIndex = updatedFormsList.findIndex(form => form.id === id);
            if (formIndex === -1) return; // No matching form found
            updatedFormsList[formIndex].is_display = checked ? STATUS.IS_DISPLAY : STATUS.IS_NOT_DISPLAY;
            setFormsList(updatedFormsList); // Update forms list

            // Update selectedJotForms for regular form
            const changedIndex = updatedSelectedJotForms.findIndex(form => form.jotforms_id === id);
            if (checked) {
                if (changedIndex === -1) {
                    updatedSelectedJotForms.push({
                        jotforms_id: id,
                        slug: updatedFormsList[formIndex].slug,
                        title: updatedFormsList[formIndex].title,
                        form_url: updatedFormsList[formIndex].url,
                        type: TYPE.JOT_FORMS,
                    });
                }
            } else {
                if (changedIndex !== -1) {
                    updatedSelectedJotForms.splice(changedIndex, 1);
                }
            }
            setSelectedJotForms(updatedSelectedJotForms); // Update selected jotforms
        }
        // Only set dirty if the selection actually changed
        if (JSON.stringify(selectedJotForms) !== JSON.stringify(updatedSelectedJotForms)) {
            setIsJotFormsModified(true);
        }
    };

    // Handle input change
    const handleUserFormFieldChange = (e, index, key) => {
        const { value } = e.target;
        const updatedForms = [...userForms];
        if (key === 1) {
            updatedForms[index]['title'] = value;
        }
        if (key === 2) {
            updatedForms[index]['form_url'] = value;
        }
        setUserForms(updatedForms);
        setIsDirty(true);
    };

    const addNewUserForm = () => {
        const hasBlankTitleKey = userForms.some((rule) => rule.title.trim() === '');
        if (hasBlankTitleKey) {
            Notification.addNotificationError(ERROR_MESSAGES.FILL_TITLE);
            return;
        }
        const hasBlankKey = userForms.some((rule) => rule.form_url.trim() === '');
        if (hasBlankKey) {
            Notification.addNotificationError(ERROR_MESSAGES.FILL_URL);
            return;
        }
        const updatedforms = [...userForms, customFormTemplate];
        setUserForms(updatedforms);
    };

    const handleInputChange = () => {
        setIsDirty(true);
    };

    const handleFormSubmit = useCallback(
        async (values) => {
            // Check for incomplete custom forms
            if (submitType == TYPE.CUSTOM_FORMS) {
                const hasBlankTitleKey = userForms.some((rule) => rule.title.trim() === '');
                if (hasBlankTitleKey) {
                    Notification.addNotificationError(ERROR_MESSAGES.FILL_TITLE);
                    return false;
                }
            }
            let filledUserForms = userForms.filter((customForms) => customForms.title.trim() && customForms.form_url.trim());
            const userFormsJson = submitType != TYPE.JOT_FORMS ? JSON.stringify(filledUserForms.length ? filledUserForms : null) : null;
            const jotFormsJson = submitType != TYPE.CUSTOM_FORMS ? JSON.stringify(selectedJotForms) : null;
            let requestData = {
                ...values,
                host_slug: hostSlug,
                userForms: userFormsJson,
                jotForms: jotFormsJson,
                type: submitType
            };
            const updateFormFunction = async () => {
                try {
                    setSpinnerLoader(true);
                    const updateFormResponse = await dispatch(
                        saveUserForms(requestData, (response) => {
                            return response;
                        }),
                    );
                    if (updateFormResponse?.status !== HttpStatus.HTTP_OK) {
                        Notification.addNotificationError(updateFormResponse.message);
                    } else {
                        Notification.addNotificationSuccess(updateFormResponse.message);
                        fetchFormsData();
                        setUserForms([customFormTemplate]);
                        // This change has been added by diya's suggestion
                        setIsJotFormsModified(false);
                        setIsCustomFormModalOpen(false);
                    }
                } catch (error) {
                } finally {
                    setSpinnerLoader(false);
                }
            }
            updateFormFunction();
        },
        [dispatch, userForms, selectedJotForms, submitType],
    );

    const deleteUserForm = async (index) => {
        const deleteMapVal = [...userForms];
        if (deleteMapVal[index] && deleteMapVal[index].slug) {
            const response = await fetchConnectedListingForms(deleteMapVal[index].slug);
            if (response) {
                Notification.addNotificationError(ERROR_MESSAGES.FORM_CANNOT_BE_DELETED);
                return;
            }
        }
        deleteMapVal.splice(index, 1);
        setUserForms(deleteMapVal);
    };

    const checkValidation = (type) => {
        if (type == TYPE.CUSTOM_FORMS) {
            const hasBlankTitleKey = userForms.some((rule) => rule.title === '');
            const hasBlankURLKey = userForms.some((rule) => rule.form_url.trim() === '');
            const hasInvalidUrl = userForms.some((rule) => rule.form_url && !isValidUrl(rule.form_url));
            setTitleRequired(hasBlankTitleKey);
            setURLRequired(hasBlankURLKey);
            if (!hasBlankTitleKey && !hasBlankURLKey && !hasInvalidUrl) {
                form.submit(); // Now handleFormSubmit will run
            }
        } else {
            if (!isJotFormsModified) {
                Notification.addNotificationError(ERROR_MESSAGES.FORM_NOT_CHANGES);
                return false;
            }
            form.validateFields().then(() => {
                form.submit();
            }).catch(errorInfo => {
            });
            setTimeout(() => {
                const errorElements = document.querySelectorAll(".ant-form-item-has-error");
                if (errorElements.length > 0) {
                    const firstErrorElement = errorElements[0] ?? 0;
                    firstErrorElement.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "start"
                    });
                }
            }, 300);
        }
    };

    const isValidUrl = (value) => {
        if (!value) return true; // allow empty (not required)
        try {
            // Check if the URL is valid and uses HTTP or HTTPS
            const url = new URL(value.trim()); // trim extra spaces
            return url.protocol === 'https:' && url.host === 'form.jotform.com';
        } catch {
            return false; // Return false if URL is invalid
        }
    };

    const handleFormEnable = async (isOnStatus, id, slug) => {
        if (!isOnStatus && slug) {
            // Only check if turning OFF for custom forms (slug)
            const response = await fetchConnectedListingForms(slug);
            if (response) {
                // Form is connected → block turning off
                Notification.addNotificationError(ERROR_MESSAGES.FORM_CANNOT_BE_TURNED_OFF);
                return;
            }
        }
        handleJotFormToggle(isOnStatus, id, slug);
    };

    const fetchConnectedListingForms = async (slug) => {
        try {
            const requestData = {
                host_slug: hostSlug,
                form_slug: slug
            };
            const response = await dispatch(
                checkFormsConnectedToListings(requestData, (res) => {
                    return res;
                }),
            );
            if (response?.status !== HttpStatus.HTTP_OK) {
                Notification.addNotificationError(response.message);
            } else {
                return response.message;
            }
        } catch (error) {
        }
    };

    // Preprocess formsList into a map for efficient access by form id
    const formsDisplayMap = useMemo(() => {
        const map = new Map();
        formsList.forEach(form => {
            // Use slug as the key if the id is null
            const key = form.id ?? form.slug;  // Use slug for custom forms (id === null)
            map.set(key, form.is_display === STATUS.IS_DISPLAY); // Store true/false status
        });
        return map;
    }, [formsList]);

    const handleCancel = () => {
        setIsCustomFormModalOpen(false); // Close the modal
        setUserForms([customFormTemplate]); // Update the forms state
        setTitleRequired(false);
        setURLRequired(false);
    };


    return (
        <>
            <Form form={form} name="Form" onFinish={handleFormSubmit} onValuesChange={handleInputChange}>
                <Cards xs={24} headless className="borderRadius common_fiter_table">
                    <div className='user_form_io' >
                        <div className="userforms-add-jotform-wrap">
                        <Button
                            size="default"
                            type="primary"
                            onClick={() => setIsCustomFormModalOpen(true)}
                            className="userforms-add-jotform-btn"
                        >
                            + Add Host's Own JotForm
                        </Button>
                        </div>
                        <Modal
                            title={
                                <div className='custom_form_save_btn'>
                                    <span>Secondary Form</span>
                                    {/* <Button
                                        onClick={() => {
                                            setSubmitType(TYPE.CUSTOM_FORMS);
                                            checkValidation(TYPE.CUSTOM_FORMS);
                                        }}
                                        htmlType="submit"
                                        type="primary"
                                        size="default"
                                        shape="circle"
                                        className="export-save-button w-20"
                                    >
                                        {spinnerLoader ? 'Loading...' : buttonName}
                                    </Button> */}
                                </div>
                            }
                            open={isCustomFormModalOpen}
                            onCancel={handleCancel}
                            footer={null}
                            // width={800}
                            className="userforms-custom-modal"
                        >
                            <BasicFormWrapper >
                                <div className="addnewbtn flex-wrapper">
                                    {userForms.map((value, index) => (
                                        <div className="flex-row" key={index}>
                                            <div className="flex-col flex-col-title">
                                                <div className="project-modal">
                                                    <Form.Item
                                                        label={
                                                            <span>
                                                                <span className="requiredFiled">*</span> Secondary Form Title
                                                            </span>
                                                        }
                                                    >
                                                        <Input
                                                            placeholder="Enter form title"
                                                            onChange={(e) => handleUserFormFieldChange(e, index, 1)}
                                                            value={value.title}
                                                            id={`custom_forms_title_${index}`}
                                                        />
                                                    {isTitleRequired && value.title === '' && (
                                                        <div className="showImageError">
                                                            <div className="ant-form-item-has-error ant-form-item-explain-error">
                                                                Please enter form title
                                                            </div>
                                                        </div>
                                                    )}
                                                    </Form.Item>
                                                </div>
                                            </div>

                                            <div className="flex-col flex-col-url">
                                                <div className="project-modal">
                                                    <Form.Item
                                                        label={
                                                            <span>
                                                                <span className="requiredFiled">*</span> Jotform URL
                                                            </span>
                                                        }
                                                    >
                                                        <Input
                                                            placeholder="Enter form URL"
                                                            onChange={(e) => handleUserFormFieldChange(e, index, 2)}
                                                            value={value.form_url}
                                                            id={`custom_forms_label_${index}`}
                                                        />
                                                    {isURLRequired && (value.form_url === '' || value.form_url === null) && (
                                                        <div className="showImageError">
                                                            <div className="ant-form-item-has-error ant-form-item-explain-error">
                                                                Please enter form URL
                                                            </div>
                                                        </div>
                                                    )}
                                                    {!isValidUrl(value.form_url) && (
                                                        <div className="showImageError">
                                                            <div className="ant-form-item-has-error ant-form-item-explain-error">
                                                                {ERROR_MESSAGES.FORM_URL_NOT_VALID}
                                                            </div>
                                                        </div>
                                                    )}
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <div className="flex-col-delete btn-align delete_icon_btn">
                                                {userForms.length > 1 && (
                                                    <Button
                                                        size="default"
                                                        outlined
                                                        type="error"
                                                        onClick={() => deleteUserForm(index)}
                                                    >
                                                        <UilTrashAlt />
                                                    </Button>
                                                )}
                                            </div>
                                            <Row gutter={15} className="mt-20">
                                                <Col xs={24}>
                                                    <Button htmlType="submit" size="default" type="dark" shape="circle" key="submit" className="w-100"
                                                        onClick={() => {
                                                            setSubmitType(TYPE.CUSTOM_FORMS);
                                                            checkValidation(TYPE.CUSTOM_FORMS);
                                                        }}>
                                                        {spinnerLoader ? 'Loading...' : buttonName}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}

                                    {/* Start Add new forms part  */}
                                    {/* <div className="flex-add-btn mt-20">
                                        <div className="project-modal">
                                            <Button
                                                size="default"
                                                outlined
                                                type="primary"
                                                key="submit"
                                                onClick={() => addNewUserForm()}
                                            >
                                                + Add Custom Form
                                            </Button>
                                        </div>
                                    </div> */}
                                    {/* End Add new forms part  */}
                                </div>
                            </BasicFormWrapper>
                        </Modal>
                        <Divider className='mb-0 mt-0' />
                        <Row gutter={15}>
                            <Spinner loader={spinnerLoader} />
                            <Cards headless className="borderRadius2">
                                <UserTableStyleWrapper>
                                    <div className="contact-table table_form_io">
                                        <TableWrapper className="table-responsive">
                                            <Table
                                                dataSource={formTableData}
                                                columns={formsTablecolumns}
                                                onChange={handleSortChange}
                                                rowKey="uuid"
                                                pagination={{
                                                    current: pagination.currentPage,
                                                    pageSize: pagination.pageSize,
                                                    total: pagination.totalItems,
                                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                                    onChange: handlePaginationChange,
                                                    onShowSizeChange: handlePageSizeChange
                                                }}
                                            />
                                        </TableWrapper>
                                    </div>
                                </UserTableStyleWrapper>
                            </Cards>
                        </Row>
                    </div>
                </Cards>
            </Form>
            <Button
                onClick={() => {
                    setSubmitType(TYPE.JOT_FORMS);
                    checkValidation(TYPE.JOT_FORMS);
                }}
                htmlType="submit"
                type="primary"
                size="default"
                shape="circle"
                className="export-save-button w-20"
            >
                {spinnerLoader ? 'Loading...' : buttonName}
            </Button>
        </>
    );
};

export default UserForms;
